// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import type { AutodeskExternalId, MachineElement } from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';

import { getDbIdsFromSpool } from 'components/Autodesk/extensions/SpoolsExtension/helpers';
import { useAutodeskExtensionStoreSnapshot } from 'components/Autodesk/useAutodeskExtensionStore';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { isNotNilOrEmptyString } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';

/** Takes a machine element and finds the dbIds for its spool and parts in the viewer. */
export const useViewerMachineElement = ({ name, spool: spoolName, sourceIds }: MachineElement) => {
  const spools = useAutodeskExtensionStoreSnapshot('spools');
  const { externalIdLookup } = useAutodeskExtensionStoreSnapshot('apsViewer');

  const spoolDbIds = useMemo(() => {
    if (isNil(spools)) return null;

    if (!isNotNilOrEmptyString(spoolName)) return [];

    const spool = spools[spoolName];
    if (isNil(spool)) return null;

    return getDbIdsFromSpool(spool);
  }, [spools, spoolName]);
  useEffect(() => {
    if (isNotNil(spools) && isNotNilOrEmptyString(spoolName) && spoolDbIds === null) {
      notifications.show({
        title: `Error finding spool`,
        message: `Spool ${spoolName} was not found in this model.`,
        color: 'red',
      });
    }
  }, [spools, spoolName, spoolDbIds]);

  const sourceDbIds = useMemo(() => {
    if (isNil(externalIdLookup)) return [];
    if (!isNotNilOrEmptyString(sourceIds)) return [];
    return sourceIds
      .split(',')
      .map((maybeExternalId) => externalIdLookup[maybeExternalId.trim() as AutodeskExternalId]);
  }, [sourceIds, externalIdLookup]);
  const elementDbIds = useMemo(() => sourceDbIds.filter((dbId) => isNotNil(dbId)), [sourceDbIds]);
  useEffect(() => {
    if (sourceDbIds.some((dbId) => isNil(dbId))) {
      notifications.show({
        title: `Error finding elements for ${name}`,
        message: `Some parts of this element were not found in this model.`,
        color: 'red',
      });
    }
  }, [sourceDbIds, name]);

  return { spoolDbIds, elementDbIds };
};
